// shabba-dashboard/src/pages/AnalysisPage.tsx
// Trading Intelligence — engine state, signals, and event narrative

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Flame } from 'lucide-react';
import { useMarketData, useEngine, useTrading } from '../context/MarketContext';
import SimpleVWAPChart from '../components/SimpleVWAPChart';
import { MLPredictionsDisplay } from '../components/analytics/MLPredictionsDisplay';
import { XAxis, YAxis, Tooltip as RechartsTip, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts';
import type { EngineRegime } from '../types';
import { useAuth } from '../context/AuthContext';
import TimeRangePicker from '../components/TimeRangePicker';
import { Panel } from '../components/ui';
import { fetchDepthImbalanceRange, fetchOfiRange } from '../api/series';
import type { TimeRange } from '../hooks/useTimeSeries';

// ── Helpers ───────────────────────────────────────────────────────────────────

const REGIME_COLORS: Record<EngineRegime, string> = {
  ACTIVE:       '#36c98e',
  accumulation: '#36c98e',
  QUIET:        '#4da3ff',
  neutral:      '#4da3ff',
  DORMANT:      '#7a8693',
  VOLATILE:     '#ff5c5c',
  distribution: '#ff5c5c',
};

const REGIME_LABEL: Record<EngineRegime, string> = {
  ACTIVE:       'ACTIVE',
  accumulation: 'ACCUM',
  QUIET:        'QUIET',
  neutral:      'NEUTRAL',
  DORMANT:      'DORMANT',
  VOLATILE:     'VOLATILE',
  distribution: 'DISTRIB',
};

const REGIME_DESC: Record<EngineRegime, string> = {
  ACTIVE:       'High activity · tight spreads · momentum building',
  accumulation: 'Steady buying pressure · spreads normal',
  QUIET:        'Low volatility · wider spreads · range-bound',
  neutral:      'Balanced flow · moderate spreads',
  DORMANT:      'Minimal activity · stale book · low liquidity',
  VOLATILE:     'Elevated risk · sharp moves · spread widening',
  distribution: 'Selling pressure · positions unwinding',
};

const KIND_COLORS: Record<string, string> = {
  REGIME_SHIFT:  '#4da3ff',
  SPRINT_WINNER: '#ff8c3a',
  F1K_SPIKE:     '#ff8c3a',
  MODEL_UPDATE:  '#a855f7',
  RISK_LIMIT:    '#ff5c5c',
};

const KIND_LABEL: Record<string, string> = {
  REGIME_SHIFT:  'REGIME',
  SPRINT_WINNER: 'SPRINT',
  F1K_SPIKE:     'F1K',
  MODEL_UPDATE:  'MODEL',
  RISK_LIMIT:    'RISK',
};

const INTELLIGENCE_KINDS = new Set(['REGIME_SHIFT', 'SPRINT_WINNER', 'F1K_SPIKE', 'MODEL_UPDATE', 'RISK_LIMIT']);

function relTime(iso: string): string {
  const diff = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AnalysisPage() {
  const { selectedSymbol, setSelectedSymbol, labelForSecId, INSTRUMENTS, analytics, orderBook, analyticsImbalanceBySymbol, getAnalyticsHistory } = useMarketData();
  const { regimeBySymbol, sprintState, activityFeed, modelHealth, performanceSnapshot } = useEngine();
  const { portfolio, orders } = useTrading();

  // Analytics defaults
  const {
    depthImbalance,
    orderFlowImbalance,
    fillProbability,
    volatility,
    staleness,
    microprice,
    riskMetrics,
  } = analytics || {
    depthImbalance:     { topN: 5, bidVolume: 0, askVolume: 0, imbalance: 0, imbalancePercent: 0 },
    orderFlowImbalance: { flowRatio: 0, buyFlow: 0, sellFlow: 0, netFlow: 0, timeWindow: 60 },
    fillProbability:    { probability: 0, queuePosition: 0, estimatedWaitTime: 0, confidence: 'low' as const },
    volatility:         { realizedVol: 0, impliedVol: 0, volatilityPercentile: 0, recommendedSpread: 0, spreadMultiplier: 1 },
    staleness:          { lastUpdateAge: 0, lastTradeAge: 0, isStale: false, stalenessLevel: 'fresh' as const },
    microprice:         { microprice: 0, bidPrice: 0, askPrice: 0, bidSize: 0, askSize: 0, spread: 0 },
    riskMetrics:        { inventoryRisk: 0, priceRisk: 0, liquidityRisk: 0, overallRisk: 'low' as const, maxExposure: 0, recommendedPosition: 0 },
  };

  const { token } = useAuth();

  // Imbalance chart history — driven by MarketContext which accumulates for ALL symbols
  type Pt = { t: number; v: number };
  const [depthHistory, setDepthHistory] = useState<Pt[]>([]);
  const [flowHistory,  setFlowHistory]  = useState<Pt[]>([]);

  // Historical range state for depth/OFI charts
  const [depthRange, setDepthRange] = useState<TimeRange>('live');
  const [ofiRange,   setOfiRange]   = useState<TimeRange>('live');
  const [histDepth,  setHistDepth]  = useState<Pt[] | null>(null);
  const [histOfi,    setHistOfi]    = useState<Pt[] | null>(null);

  useEffect(() => {
    if (depthRange === 'live') { setHistDepth(null); return; }
    let cancelled = false;
    fetchDepthImbalanceRange(selectedSymbol, depthRange, token)
      .then(pts => {
        if (!cancelled) setHistDepth(
          pts.map(p => ({ t: new Date(p.ts).getTime(), v: p.imbalance ?? p.avg_depth_imbalance ?? 0 }))
        );
      })
      .catch(() => { if (!cancelled) setHistDepth([]); });
    return () => { cancelled = true; };
  }, [selectedSymbol, depthRange, token]);

  useEffect(() => {
    if (ofiRange === 'live') { setHistOfi(null); return; }
    let cancelled = false;
    fetchOfiRange(selectedSymbol, ofiRange, token)
      .then(pts => {
        if (!cancelled) setHistOfi(
          pts.map(p => ({ t: new Date(p.ts).getTime(), v: p.ofi ?? p.avg_ofi ?? 0 }))
        );
      })
      .catch(() => { if (!cancelled) setHistOfi([]); });
    return () => { cancelled = true; };
  }, [selectedSymbol, ofiRange, token]);

  // Sync display state from the shared history (symbol switch or replay data arrival)
  const syncHistory = () => {
    const h = getAnalyticsHistory(selectedSymbol);
    setDepthHistory(h.map(p => ({ t: p.t, v: p.depthImbalance })));
    setFlowHistory(h.map(p => ({ t: p.t, v: p.ofi })));
  };

  // On symbol switch — restore history for new symbol
  useEffect(() => { syncHistory(); }, [selectedSymbol]); // eslint-disable-line react-hooks/exhaustive-deps

  // On replay data arrival — re-read to get historical backfill
  useEffect(() => { syncHistory(); }, [analyticsImbalanceBySymbol]); // eslint-disable-line react-hooks/exhaustive-deps

  // On live analytics update — re-read (MarketContext already appended the new point)
  useEffect(() => {
    if (analytics?.depthImbalance) { syncHistory(); }
  }, [analytics?.depthImbalance?.imbalance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick formatter for depth/OFI charts — shows date for multi-day ranges, time otherwise
  const fmtTick = (range: string) => (v: number) => {
    const d = new Date(v);
    if (range === '1m' || range === '3m' || range === '1y')
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    if (range === '1w')
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  const fmtLabel = (v: any) => new Date(Number(v)).toLocaleString();

  // Regime for selected symbol
  const regimeState = regimeBySymbol.get(selectedSymbol) ?? regimeBySymbol.get(labelForSecId(selectedSymbol));
  const regimeColor = regimeState ? REGIME_COLORS[regimeState.regime as EngineRegime] : '#2a3540';
  const regimeLabel = regimeState ? REGIME_LABEL[regimeState.regime as EngineRegime] : 'NO DATA';
  const regimeDesc  = regimeState ? REGIME_DESC[regimeState.regime as EngineRegime]  : 'Awaiting regime classification';

  // Sprint data
  const winnerSet = new Set(sprintState?.winners ?? []);
  const sortedSprintTickers = sprintState
    ? [...sprintState.tickers].sort((a, b) => b.sprint_score - a.sprint_score)
    : [];

  // Order flow
  const totalFlow = orderFlowImbalance.buyFlow + orderFlowImbalance.sellFlow;
  const buyPct = totalFlow > 0 ? (orderFlowImbalance.buyFlow / totalFlow) * 100 : 50;

  // Intelligence feed (engine-only kinds, no fills/alerts)
  const intelligenceFeed = useMemo(
    () => activityFeed.filter(e => INTELLIGENCE_KINDS.has(e.kind)).slice(0, 30),
    [activityFeed],
  );

  // Liquidity heatmap: aggregate individual orders into price levels
  const bidLevels = useMemo(() => {
    const map = new Map<number, number>();
    (orderBook?.bids ?? []).forEach(b => map.set(b.price, (map.get(b.price) ?? 0) + b.size));
    return Array.from(map.entries()).map(([price, size]) => ({ price, size })).sort((a, b) => b.price - a.price).slice(0, 12);
  }, [orderBook?.bids]);

  const askLevels = useMemo(() => {
    const map = new Map<number, number>();
    (orderBook?.asks ?? []).forEach(a => map.set(a.price, (map.get(a.price) ?? 0) + a.size));
    return Array.from(map.entries()).map(([price, size]) => ({ price, size })).sort((a, b) => a.price - b.price).slice(0, 12);
  }, [orderBook?.asks]);

  const heatmapMax = useMemo(
    () => Math.max(...bidLevels.map(b => b.size), ...askLevels.map(a => a.size), 1),
    [bidLevels, askLevels]
  );

  // Spread signal
  const spreadBps = microprice.spread > 0 && microprice.microprice > 0
    ? (microprice.spread / microprice.microprice) * 10000
    : 0;
  const recSpreadBps = volatility.recommendedSpread;
  const spreadStatus = !analytics ? '—'
    : spreadBps < recSpreadBps * 0.8 ? 'TIGHT'
    : spreadBps > recSpreadBps * 1.2 ? 'WIDE'
    : 'COMPETITIVE';
  const spreadStatusColor = spreadStatus === 'COMPETITIVE' ? '#36c98e'
    : spreadStatus === 'TIGHT' ? '#ff8c3a'
    : spreadStatus === '—' ? '#2a3540'
    : '#ff5c5c';

  // Risk helpers
  const riskColor = (v: number) => v > 0.7 ? '#ff5c5c' : v > 0.4 ? '#ff8c3a' : '#36c98e';
  const riskLabel = (v: number) => v > 0.7 ? 'HIGH' : v > 0.4 ? 'MED' : 'LOW';
  const maxRisk = Math.max(riskMetrics.inventoryRisk, riskMetrics.priceRisk, riskMetrics.liquidityRisk);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Zone 1: Engine State Strip ── */}
      <Panel
        title="Engine state · all instruments"
        badge={sortedSprintTickers.length > 0
          ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Flame size={10} color="#ff8c3a" />
              Sprint: {sortedSprintTickers.slice(0, 3).map(t => `${t.ticker} ${t.sprint_score.toFixed(2)}`).join(' · ')}
            </span>
          )
          : undefined}
        flush
      >
        <div style={{ display: 'flex', overflowX: 'auto', padding: '8px 12px', gap: 6 }}>
          {INSTRUMENTS.map(inst => {
            const rs = regimeBySymbol.get(inst.secId) ?? regimeBySymbol.get(inst.label);
            const color = rs ? REGIME_COLORS[rs.regime as EngineRegime] : '#2a3540';
            const lbl   = rs ? REGIME_LABEL[rs.regime as EngineRegime] : '—';
            const isSelected = selectedSymbol === inst.secId;
            const isWinner = winnerSet.has(inst.secId) || winnerSet.has(inst.label);
            const sprTicker = sortedSprintTickers.find(t => t.ticker === inst.label || t.ticker === inst.secId);
            return (
              <div
                key={inst.secId}
                onClick={() => setSelectedSymbol(inst.secId)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '6px 10px', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                  backgroundColor: isSelected ? 'rgba(77,163,255,0.1)' : 'transparent',
                  border: `1px solid ${isSelected ? '#4da3ff' : 'transparent'}`,
                  minWidth: 72,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#d7dde4' : '#cbd5e1' }}>{inst.label}</span>
                  {isWinner && <Flame size={10} color="#ff8c3a" />}
                </div>
                <div style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                  backgroundColor: `${color}20`, color, letterSpacing: '0.04em',
                }}>
                  {lbl}
                </div>
                {rs && (
                  <span style={{ fontSize: 9, color: '#46525e' }}>{(rs.confidence * 100).toFixed(0)}%</span>
                )}
                {sprTicker && (
                  <span style={{ fontSize: 9, color: '#7a8693' }}>F1K {sprTicker.f1k_score.toFixed(1)}</span>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {/* ── Zone 2: Deep Dive (3fr left + 2fr right) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>

        {/* Left: Chart + Signals */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* VWAP Chart with microprice overlay */}
          <SimpleVWAPChart
            symbol={selectedSymbol}
            height={200}
            microprice={microprice.microprice > 0 ? microprice.microprice : undefined}
          />

          {/* Weighted Depth Imbalance — time-series chart + current stats */}
          <Panel title={`Weighted depth imbalance · ${labelForSecId(selectedSymbol)} · top ${depthImbalance.topN}`}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {analytics && depthRange === 'live' && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                    padding: '2px 7px', borderRadius: 3,
                    backgroundColor: depthImbalance.imbalance > 0.1 ? '#36c98e20' : depthImbalance.imbalance < -0.1 ? '#ff5c5c20' : '#4da3ff20',
                    color: depthImbalance.imbalance > 0.1 ? '#36c98e' : depthImbalance.imbalance < -0.1 ? '#ff5c5c' : '#4da3ff',
                  }}>
                    {depthImbalance.imbalance > 0.1 ? '▲ BID HEAVY' : depthImbalance.imbalance < -0.1 ? '▼ ASK HEAVY' : '● BALANCED'}
                  </span>
                )}
                <TimeRangePicker value={depthRange} onChange={setDepthRange} />
              </div>
            </div>
            {/* Time-series chart */}
            <div style={{ height: 110, marginBottom: 8 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={histDepth ?? depthHistory} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                  <defs>
                    <linearGradient id="diFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    tickFormatter={fmtTick(depthRange)} tick={{ fontSize: 8, fill: '#7a8693' }}
                    tickCount={5} minTickGap={40} />
                  <YAxis domain={[-1, 1]} tick={{ fontSize: 8, fill: '#2a3540' }} tickCount={3} />
                  <ReferenceLine y={0} stroke="#ff8c3a" strokeDasharray="4 3" strokeWidth={1} />
                  <RechartsTip
                    formatter={(v: any) => [(Number(v)).toFixed(3), 'Imbalance']}
                    labelFormatter={fmtLabel}
                    contentStyle={{ background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 6, fontSize: 11 }}
                  />
                  <Area type="monotone" dataKey="v" stroke="#ec4899" strokeWidth={1.5}
                    fill="url(#diFill)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Current snapshot stats */}
            {(() => {
              const total = depthImbalance.bidVolume + depthImbalance.askVolume;
              const bPct = total > 0 ? (depthImbalance.bidVolume / total) * 100 : 50;
              const aPct = 100 - bPct;
              const absPct = Math.abs(depthImbalance.imbalancePercent);
              return (
                <>
                  <div style={{ display: 'flex', height: 14, borderRadius: 3, overflow: 'hidden', marginBottom: 5 }}>
                    <div style={{ width: `${bPct}%`, backgroundColor: '#36c98e', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 3 }}>
                      {bPct > 15 && <span style={{ fontSize: 8, color: 'white', fontWeight: 700 }}>{bPct.toFixed(0)}%</span>}
                    </div>
                    <div style={{ width: `${aPct}%`, backgroundColor: '#ff5c5c', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 3 }}>
                      {aPct > 15 && <span style={{ fontSize: 8, color: 'white', fontWeight: 700 }}>{aPct.toFixed(0)}%</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 5 }}>
                    <span style={{ color: '#36c98e' }}>Bid {depthImbalance.bidVolume.toLocaleString()}</span>
                    <span style={{ color: analytics ? (absPct > 15 ? '#ff8c3a' : '#7a8693') : '#2a3540', fontWeight: 600 }}>
                      {analytics ? `${depthImbalance.imbalancePercent > 0 ? '+' : ''}${depthImbalance.imbalancePercent.toFixed(1)}%` : '—'}
                    </span>
                    <span style={{ color: '#ff5c5c' }}>Ask {depthImbalance.askVolume.toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#46525e', fontStyle: 'italic' }}>
                    {depthImbalance.imbalance > 0.3 ? 'Strong bid-side pressure — expect ask-side resistance'
                     : depthImbalance.imbalance < -0.3 ? 'Strong ask-side pressure — expect bid-side support thin'
                     : depthImbalance.imbalance > 0.1 ? 'Mild bid bias — monitor for directional follow-through'
                     : depthImbalance.imbalance < -0.1 ? 'Mild ask bias — spreads may widen on buy-side fills'
                     : 'Book balanced — standard spread regime'}
                  </div>
                </>
              );
            })()}
          </Panel>

          {/* Liquidity Heatmap */}
          {(bidLevels.length > 0 || askLevels.length > 0) && (
            <Panel title="Liquidity heatmap" badge={labelForSecId(selectedSymbol)}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#36c98e', letterSpacing: '0.08em' }}>BIDS</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#ff5c5c', letterSpacing: '0.08em', textAlign: 'right' }}>ASKS</span>
              </div>
              {/* Col sub-headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 48px 1px 48px 1fr 52px', marginBottom: 3 }}>
                <span style={{ fontSize: 9, color: '#46525e', textAlign: 'right', paddingRight: 4 }}>PRICE</span>
                <span />
                <span style={{ fontSize: 9, color: '#46525e', textAlign: 'right' }}>SIZE</span>
                <div style={{ backgroundColor: '#2a3540' }} />
                <span style={{ fontSize: 9, color: '#46525e' }}>SIZE</span>
                <span />
                <span style={{ fontSize: 9, color: '#46525e', paddingLeft: 4 }}>PRICE</span>
              </div>
              {/* Rows */}
              {Array.from({ length: Math.max(bidLevels.length, askLevels.length) }).map((_, i) => {
                const bid = bidLevels[i];
                const ask = askLevels[i];
                const bidPct = bid ? (bid.size / heatmapMax) * 100 : 0;
                const askPct = ask ? (ask.size / heatmapMax) * 100 : 0;
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 48px 1px 48px 1fr 52px', alignItems: 'center', height: 19 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#36c98e', textAlign: 'right', paddingRight: 4, fontVariantNumeric: 'tabular-nums' }}>
                      {bid ? bid.price.toFixed(2) : ''}
                    </span>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', height: 11 }}>
                      {bid && <div style={{ width: `${bidPct}%`, height: '100%', backgroundColor: 'rgba(54,201,142,0.35)', borderRadius: '2px 0 0 2px' }} />}
                    </div>
                    <span style={{ fontSize: 10, color: '#7a8693', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {bid ? bid.size.toLocaleString() : ''}
                    </span>
                    <div style={{ backgroundColor: '#2a3540', height: '100%' }} />
                    <span style={{ fontSize: 10, color: '#7a8693', fontVariantNumeric: 'tabular-nums', paddingLeft: 2 }}>
                      {ask ? ask.size.toLocaleString() : ''}
                    </span>
                    <div style={{ height: 11 }}>
                      {ask && <div style={{ width: `${askPct}%`, height: '100%', backgroundColor: 'rgba(255,92,92,0.35)', borderRadius: '0 2px 2px 0' }} />}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#ff5c5c', paddingLeft: 4, fontVariantNumeric: 'tabular-nums' }}>
                      {ask ? ask.price.toFixed(2) : ''}
                    </span>
                  </div>
                );
              })}
            </Panel>
          )}

          {/* Order Flow Imbalance — time-series chart + current stats */}
          <Panel
            title="Order flow imbalance"
            badge={ofiRange === 'live'
              ? `rolling ${orderFlowImbalance.timeWindow}s · ${labelForSecId(selectedSymbol)}`
              : undefined}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 6 }}>
              <TimeRangePicker value={ofiRange} onChange={setOfiRange} />
            </div>
            {/* Time-series chart */}
            <div style={{ height: 110, marginBottom: 8 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={histOfi ?? flowHistory} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                  <defs>
                    <linearGradient id="ofFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    tickFormatter={fmtTick(ofiRange)} tick={{ fontSize: 8, fill: '#7a8693' }}
                    tickCount={5} minTickGap={40} />
                  <YAxis domain={ofiRange === 'live' ? [-1, 1] : ['auto', 'auto']} tick={{ fontSize: 8, fill: '#2a3540' }} tickCount={3} />
                  <ReferenceLine y={0} stroke="#ff8c3a" strokeDasharray="4 3" strokeWidth={1} />
                  <RechartsTip
                    formatter={(v: any) => [(Number(v)).toFixed(3), 'Flow Imbalance']}
                    labelFormatter={fmtLabel}
                    contentStyle={{ background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 6, fontSize: 11 }}
                  />
                  <Area type="monotone" dataKey="v" stroke="#ec4899" strokeWidth={1.5}
                    fill="url(#ofFill)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Current snapshot stats */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 4 }}>
              <span style={{ color: '#36c98e', fontWeight: 700 }}>BUY {buyPct.toFixed(0)}%</span>
              <span style={{ color: '#7a8693' }}>←  →</span>
              <span style={{ color: '#ff5c5c', fontWeight: 700 }}>SELL {(100 - buyPct).toFixed(0)}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#2a3540', display: 'flex', marginBottom: 6 }}>
              <div style={{ width: `${buyPct}%`, backgroundColor: '#36c98e', transition: 'width 0.4s' }} />
              <div style={{ flex: 1, backgroundColor: '#ff5c5c' }} />
            </div>
            <div style={{ fontSize: 10, color: '#7a8693', textAlign: 'center' }}>
              Net: <span style={{ color: orderFlowImbalance.netFlow >= 0 ? '#36c98e' : '#ff5c5c', fontWeight: 600 }}>
                {orderFlowImbalance.netFlow >= 0 ? '+' : ''}{orderFlowImbalance.netFlow.toLocaleString()}
              </span>
            </div>
          </Panel>

        </div>

        {/* Right: Engine Decision Blocks */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Block A — Regime */}
          <Panel title="Regime" badge={labelForSecId(selectedSymbol)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: regimeColor, flexShrink: 0 }} />
              <span style={{ fontSize: 22, fontWeight: 700, color: regimeColor, lineHeight: 1 }}>{regimeLabel}</span>
            </div>
            {regimeState && (
              <>
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#7a8693', marginBottom: 3 }}>
                    <span>Confidence</span>
                    <span style={{ color: regimeColor, fontWeight: 600 }}>{(regimeState.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div style={{ height: 4, backgroundColor: '#2a3540', borderRadius: 2 }}>
                    <div style={{ height: 4, width: `${regimeState.confidence * 100}%`, backgroundColor: regimeColor, borderRadius: 2, transition: 'width 0.4s' }} />
                  </div>
                </div>
                {regimeState.prev_regime && (
                  <div style={{ fontSize: 10, color: '#46525e', marginBottom: 6 }}>
                    ← {REGIME_LABEL[regimeState.prev_regime as EngineRegime]} · {relTime(regimeState.last_changed)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
                  {regimeState.spread_multiplier != null && (
                    <span style={{ color: '#7e8a96' }}>
                      Spread <span style={{ color: '#d7dde4', fontWeight: 700 }}>×{regimeState.spread_multiplier.toFixed(1)}</span>
                    </span>
                  )}
                  {regimeState.size_multiplier != null && (
                    <span style={{ color: '#7e8a96' }}>
                      Size <span style={{ color: '#d7dde4', fontWeight: 700 }}>×{regimeState.size_multiplier.toFixed(1)}</span>
                    </span>
                  )}
                </div>
              </>
            )}
            <div style={{ marginTop: 8, fontSize: 10, color: '#7a8693', fontStyle: 'italic' }}>{regimeDesc}</div>
          </Panel>

          {/* ML Predictions (regime + fill prob from SIGNALS / ML_PREDICTION / ANALYTICS.ml) */}
          <MLPredictionsDisplay mlData={analytics?.ml} />

          {/* Block B — Fill Probability (bid / ask split) */}
          <Panel
            title="Fill probability"
            badge={`${staleness.isStale ? 'STALE' : 'FRESH'}${staleness.lastUpdateAge > 0 ? ` · ${staleness.lastUpdateAge.toFixed(0)}s` : ''}`}
            badgeTone={staleness.isStale ? 'alert' : 'ok'}
          >
            {(() => {
              const hasSplit = analytics && fillProbability.bidProbability != null && fillProbability.askProbability != null;
              const probColor = (p: number) => p > 0.5 ? '#36c98e' : p > 0.25 ? '#ff8c3a' : '#7a8693';
              if (hasSplit) {
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {/* Bid side */}
                    <div style={{ padding: '8px 10px', backgroundColor: '#0a0d10', borderRadius: 6, border: '1px solid #36c98e30' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <span style={{ fontSize: 9, color: '#36c98e', fontWeight: 700, letterSpacing: '0.06em' }}>BID</span>
                        {microprice.bidPrice > 0 && (
                          <span style={{ fontSize: 9, color: '#2a3540' }}>@ {microprice.bidPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: probColor(fillProbability.bidProbability!), marginBottom: 4 }}>
                        {(fillProbability.bidProbability! * 100).toFixed(0)}%
                      </div>
                      <div style={{ fontSize: 10, color: '#46525e' }}>
                        Queue #{fillProbability.bidQueuePosition ?? '—'}
                      </div>
                    </div>
                    {/* Ask side */}
                    <div style={{ padding: '8px 10px', backgroundColor: '#0a0d10', borderRadius: 6, border: '1px solid #ff5c5c30' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <span style={{ fontSize: 9, color: '#ff5c5c', fontWeight: 700, letterSpacing: '0.06em' }}>ASK</span>
                        {microprice.askPrice > 0 && (
                          <span style={{ fontSize: 9, color: '#2a3540' }}>@ {microprice.askPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: probColor(fillProbability.askProbability!), marginBottom: 4 }}>
                        {(fillProbability.askProbability! * 100).toFixed(0)}%
                      </div>
                      <div style={{ fontSize: 10, color: '#46525e' }}>
                        Queue #{fillProbability.askQueuePosition ?? '—'}
                      </div>
                    </div>
                  </div>
                );
              }
              // Fallback: single combined value
              return (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                  <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: analytics ? probColor(fillProbability.probability) : '#2a3540' }}>
                    {analytics ? `${(fillProbability.probability * 100).toFixed(0)}%` : '—'}
                  </span>
                  <span style={{ fontSize: 11, color: '#7a8693', paddingBottom: 4 }}>
                    Queue #{fillProbability.queuePosition || '—'}
                  </span>
                </div>
              );
            })()}
          </Panel>

          {/* Block C — Spread Signal */}
          <Panel title="Spread signal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 9, color: '#7a8693', marginBottom: 2 }}>Current</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#d7dde4' }}>
                  {analytics ? `${spreadBps.toFixed(0)} bps` : '—'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: '#7a8693', marginBottom: 2 }}>Recommended</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#7e8a96' }}>
                  {analytics ? `${recSpreadBps.toFixed(0)} bps` : '—'}
                </div>
              </div>
            </div>
            <div style={{
              padding: '3px 8px', borderRadius: 4, textAlign: 'center',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              backgroundColor: `${spreadStatusColor}15`,
              color: spreadStatusColor,
              border: `1px solid ${spreadStatusColor}30`,
            }}>
              {spreadStatus}
            </div>
          </Panel>

          {/* Block D — Risk (DSE-style with utilisation bars) */}
          <Panel
            title="Risk"
            badge={analytics ? riskLabel(maxRisk) : undefined}
            badgeTone={maxRisk > 0.7 ? 'alert' : maxRisk > 0.4 ? 'warn' : 'ok'}
          >
            {([
              { label: 'Inventory', value: riskMetrics.inventoryRisk,  desc: 'position vs limit' },
              { label: 'Price',     value: riskMetrics.priceRisk,      desc: 'mark-to-market vol' },
              { label: 'Liquidity', value: riskMetrics.liquidityRisk,  desc: 'spread cost / depth' },
            ] as const).map(r => (
              <div key={r.label} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <div>
                    <span style={{ fontSize: 10, color: '#7e8a96', fontWeight: 600 }}>{r.label}</span>
                    <span style={{ fontSize: 9, color: '#2a3540', marginLeft: 5 }}>{r.desc}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: analytics ? riskColor(r.value) : '#2a3540' }}>
                      {analytics ? `${(r.value * 100).toFixed(0)}%` : '—'}
                    </span>
                    <span style={{
                      fontSize: 8, fontWeight: 700, letterSpacing: '0.04em',
                      padding: '1px 5px', borderRadius: 3,
                      backgroundColor: analytics ? `${riskColor(r.value)}15` : 'transparent',
                      color: analytics ? riskColor(r.value) : '#2a3540',
                    }}>
                      {analytics ? riskLabel(r.value) : '—'}
                    </span>
                  </div>
                </div>
                <div style={{ height: 5, backgroundColor: '#0a0d10', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: analytics ? `${r.value * 100}%` : '0%',
                    backgroundColor: riskColor(r.value),
                    borderRadius: 3,
                    transition: 'width 0.5s',
                  }} />
                </div>
              </div>
            ))}
            {analytics && (riskMetrics.maxExposure > 0 || riskMetrics.recommendedPosition > 0) && (
              <div style={{ borderTop: '1px solid #0a0d10', marginTop: 6, paddingTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <div>
                  <div style={{ fontSize: 9, color: '#2a3540' }}>Max Exposure</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#7a8693' }}>
                    {riskMetrics.maxExposure.toLocaleString()} TZS
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#2a3540' }}>Rec. Position</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#7a8693' }}>
                    {riskMetrics.recommendedPosition.toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </Panel>

        </div>
      </div>

      {/* ── Zone 3a: ML Model Health + Performance ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* ML Model Health */}
        <Panel
          title="ML model health"
          badge={modelHealth?.drift_detected ? 'DRIFT' : undefined}
          badgeTone="alert"
          flush
        >
          {!modelHealth ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#2a3540', fontSize: 12 }}>
              Awaiting model health data…
            </div>
          ) : (
            <div style={{ padding: '8px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Fill Prob MAE', value: modelHealth.fill_prob_mae, fmt: (v: number) => v.toFixed(3) },
                { label: 'Wait Time MAE', value: modelHealth.wait_time_mae, fmt: (v: number) => `${v.toFixed(1)}m` },
                { label: 'Calibration Err', value: modelHealth.calibration_error, fmt: (v: number) => v.toFixed(3) },
                { label: 'Predictions', value: modelHealth.predictions_count, fmt: (v: number) => v.toLocaleString() },
              ].map(({ label, value, fmt }) => (
                <div key={label} style={{ padding: '6px 8px', backgroundColor: '#0a0d10', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#46525e', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: value != null ? '#cbd5e1' : '#2a3540' }}>
                    {value != null ? fmt(value) : '—'}
                  </div>
                </div>
              ))}
              {modelHealth.model_type && (
                <div style={{ gridColumn: '1 / -1', fontSize: 10, color: '#46525e', padding: '4px 8px' }}>
                  Model: <span style={{ color: '#7e8a96' }}>{modelHealth.model_type}</span>
                  {modelHealth.last_updated && <span style={{ marginLeft: 8 }}>Updated: {relTime(modelHealth.last_updated)}</span>}
                </div>
              )}
            </div>
          )}
        </Panel>

        {/* Performance Snapshot */}
        <Panel title="Performance" flush>
          {(() => {
            const realizedPnl = portfolio.totalRealizedPnL;
            const unrealizedPnl = portfolio.totalUnrealizedPnL;
            const liveFills = orders.reduce((s, o) => s + (o.fills?.length ?? 0), 0);
            const fills = liveFills > 0 ? liveFills : (performanceSnapshot?.fills_processed ?? 0);
            const maxDrawdown = performanceSnapshot?.max_drawdown ?? 0;
            const hasData = realizedPnl !== 0 || unrealizedPnl !== 0 || fills > 0;
            if (!hasData && !performanceSnapshot) return (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#2a3540', fontSize: 12 }}>
                Awaiting performance data…
              </div>
            );
            return (
              <div style={{ padding: '8px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Realized PnL', value: realizedPnl, color: realizedPnl >= 0 ? '#36c98e' : '#ff5c5c' },
                  { label: 'Unrealized PnL', value: unrealizedPnl, color: unrealizedPnl >= 0 ? '#36c98e' : '#ff5c5c' },
                  { label: 'Fills', value: fills, color: '#7e8a96' as string },
                  { label: 'Max Drawdown', value: maxDrawdown, color: '#ff8c3a' as string },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ padding: '6px 8px', backgroundColor: '#0a0d10', borderRadius: 4 }}>
                    <div style={{ fontSize: 9, color: '#46525e', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color }}>
                      {typeof value === 'number' && label.includes('PnL')
                        ? `${value >= 0 ? '+' : ''}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} TZS`
                        : typeof value === 'number' && label.includes('Drawdown')
                        ? `${(value * 100).toFixed(1)}%`
                        : value?.toLocaleString() ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Panel>
      </div>

      {/* ── Zone 3b: Engine Narrative + Sprint Leaderboard ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

        {/* Engine Narrative feed */}
        <Panel
          title="Engine narrative"
          badge={intelligenceFeed.length > 0 ? intelligenceFeed.length : undefined}
          flush
        >
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {intelligenceFeed.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#2a3540', fontSize: 12 }}>
                Waiting for engine events…
              </div>
            ) : intelligenceFeed.map(ev => {
              const color = KIND_COLORS[ev.kind] ?? '#46525e';
              const kindLbl = KIND_LABEL[ev.kind] ?? ev.kind;
              return (
                <div key={ev.id} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  padding: '8px 12px',
                  borderLeft: `3px solid ${color}`,
                  borderBottom: '1px solid #0a0d10',
                }}>
                  <div style={{
                    fontSize: 8, fontWeight: 700, letterSpacing: '0.06em',
                    color, backgroundColor: `${color}15`,
                    border: `1px solid ${color}30`,
                    borderRadius: 3, padding: '2px 5px', marginTop: 1, flexShrink: 0,
                  }}>
                    {kindLbl}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.3 }}>{ev.title}</div>
                    {ev.detail && <div style={{ fontSize: 10, color: '#7a8693', marginTop: 2 }}>{ev.detail}</div>}
                  </div>
                  <div style={{ fontSize: 10, color: '#2a3540', flexShrink: 0, paddingTop: 1 }}>{relTime(ev.timestamp)}</div>
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Sprint Leaderboard */}
        <Panel
          title="Sprint leaderboard"
          badge={sprintState
            ? `${new Date(sprintState.sprint_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${new Date(sprintState.sprint_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : undefined}
          flush
        >
          {sortedSprintTickers.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#2a3540', fontSize: 12 }}>
              No sprint data
            </div>
          ) : (
            <div>
              {/* Column headers */}
              <div style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                padding: '5px 12px', borderBottom: '1px solid #0a0d10',
              }}>
                {['Ticker', 'Sprint', 'ROI', 'F1K', 'Boost'].map(h => (
                  <div key={h} style={{ fontSize: 9, color: '#2a3540', fontWeight: 700, letterSpacing: '0.06em' }}>{h}</div>
                ))}
              </div>
              {sortedSprintTickers.map(t => (
                <div
                  key={t.ticker}
                  style={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                    padding: '7px 12px', borderBottom: '1px solid #0a0d10',
                    backgroundColor: t.is_winner ? 'rgba(255,140,58,0.04)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.is_winner ? '#ff8c3a' : '#7e8a96' }}>{t.ticker}</span>
                    {t.is_winner && <Flame size={9} color="#ff8c3a" />}
                    {t.spike_detected && <span style={{ fontSize: 8, color: '#ff5c5c', fontWeight: 700, marginLeft: 2 }}>SPIKE</span>}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.is_winner ? '#ff8c3a' : '#7a8693' }}>{t.sprint_score.toFixed(2)}</div>
                  <div style={{ fontSize: 12, color: '#7a8693' }}>{t.roi_score.toFixed(2)}</div>
                  <div style={{ fontSize: 12, color: '#7a8693' }}>{t.f1k_score.toFixed(1)}</div>
                  <div style={{ fontSize: 12, color: t.capital_boost > 1 ? '#36c98e' : '#7a8693' }}>×{t.capital_boost.toFixed(1)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

      </div>

    </div>
  );
}
